"use client";

/**
 * The building blocks the One screens are made of.
 *
 * Promoted from the prototype with Home, the first screen in the product to
 * need them. `Poster` stayed behind: it is a deterministic wash generated from
 * an id, standing in for a file the prototype does not have, and the product
 * has real thumbnails.
 *
 * The prototype's widest frame breakpoint becomes `lg` here and its roomy one
 * `md`, because a product page is measured against the window rather than a
 * simulated device. Everything else is the markup as it was settled.
 */

import { ChevronRight, Play } from "lucide-react";
import type { ReactNode } from "react";
import {
  BADGE,
  TONE,
  type Tone,
} from "@/components/ui/design-system/badge-styles";
import {
  CONTROL_PRESS,
  SURFACE,
} from "@/components/ui/design-system/surface-styles";
import {
  Body,
  Caption,
  CardTitle,
  Hint,
  Label,
  Title,
} from "@/components/ui/design-system/text";
import { cn } from "@/lib/utils";
import { PROSE, ROW_PRESS, TOUCH } from "./chrome";

/** A pane. The system's card, at the padding a 375px screen can afford. */
export function Card({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={cn(SURFACE, "p-nf-ds-4 lg:p-nf-ds-6", className)}>
      {children}
    </section>
  );
}

/**
 * A pane holding rows.
 *
 * The rows are inset and spaced rather than ruled, so the pane's own edge is
 * the only line in the picture — and each row's pressed fill has a shape of
 * its own instead of colouring a band from edge to edge.
 */
export function ListCard({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <section
      className={cn(SURFACE, "flex flex-col gap-nf-ds-1 p-nf-ds-1", className)}
    >
      {children}
    </section>
  );
}

/**
 * A tappable row: label, optional detail line, optional trailing value.
 *
 * 56px minimum rather than the 48px floor, because a row spanning the screen
 * is aimed at loosely — the finger lands somewhere in the band, not on a
 * target. The chevron is not decoration either: it is the only thing that
 * separates a row that navigates from a row that states a fact.
 *
 * NO RULE BETWEEN ROWS. The surface's own argument against a border on a pane
 * applies inside it too: a drawn line is what makes soft shapes read as boxes,
 * and a list of six hairlines is a table pretending to be a list. What tells
 * one row from the next is the gap between their text blocks, the weight of
 * the titles against the muted detail under them, and — the moment a finger or
 * a pointer is on one — a fill that shows exactly which row is being pressed.
 */
export function Row({
  title,
  detail,
  value,
  icon,
  onClick,
  className,
}: {
  title: ReactNode;
  detail?: ReactNode;
  value?: ReactNode;
  icon?: ReactNode;
  onClick?: () => void;
  /**
   * Placement only — how the row sits in its list. Never its own geometry.
   *
   * The case this exists for: a card holding a single row, stretched to match
   * its neighbours. `flex-1` makes the row take the height rather than float
   * in the middle of it, which matters because the row is the button — dead
   * padding around it is a card that looks clickable and is not.
   */
  className?: string;
}) {
  const inner = (
    <>
      {icon ? (
        <span className="grid size-[36px] shrink-0 place-items-center rounded-[11px] bg-nf-ds-surface-ground text-nf-ds-fg">
          {icon}
        </span>
      ) : null}
      {/* `item`, not 4: at the card-title role the title has enough weight
          to crowd the line under it, and 4 points read as one block of text
          rather than a name and its explanation. Matches the tiles. */}
      <span className="flex min-w-0 flex-1 flex-col gap-nf-ds-item text-left">
        {/* The `title` role, which the design system defines as the card
            title — 15/700 — rather than body text with a weight bolted on.
            This was `PROSE` plus `font-[500]`, the submit card was `PROSE`
            plus `font-[600]` and the hero was the section role, so three
            cards side by side had three different titles. */}
        <CardTitle as="span">{title}</CardTitle>
        {detail ? (
          <Hint as="span" muted className="text-pretty">
            {detail}
          </Hint>
        ) : null}
      </span>
      {value ? <span className="shrink-0">{value}</span> : null}
      {onClick ? (
        <ChevronRight
          aria-hidden="true"
          className="size-[18px] shrink-0 text-muted-foreground"
          strokeWidth={2}
        />
      ) : null}
    </>
  );

  const shell = cn(
    "flex w-full items-center gap-nf-ds-item rounded-[14px] px-nf-ds-3 py-nf-ds-3",
    TOUCH,
    className,
  );

  if (!onClick) return <div className={shell}>{inner}</div>;

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(shell, "text-left outline-hidden", ROW_PRESS)}
    >
      {inner}
    </button>
  );
}

/**
 * A tile: a glyph, a name and a line, stacked and sitting on the floor.
 *
 * NOT `Row`, which is the same three things laid out across. A row is right
 * in a list, where the eye runs down a column of titles at one left edge and
 * the chevron is what separates a row that goes somewhere from a row that
 * states a fact. In a bento there is no column to run down — the cards are a
 * grid of different sizes — and a horizontal row inside a tall cell leaves
 * the space above and below it empty with the content floating in the middle.
 *
 * Stacked and bottom-aligned instead, so however tall the grid makes a cell,
 * every tile's text sits on the same floor as its neighbours and the picture
 * reads as a set. The glyph rides just above the name rather than pinning to
 * the top: pinned, it drifts further from the words it belongs to the taller
 * the cell gets.
 *
 * NO CHEVRON. A row needs one because a list of rows contains both the kind
 * that navigate and the kind that do not. A tile is a card, the whole surface
 * presses, and at this size a chevron is a sixth thing in a cell that has
 * four.
 *
 * The glyph is bare here, where `Row` sets it in a 36px plate. The plate
 * exists to hold a row's left edge steady when some rows have icons and some
 * do not; in a grid every tile has one and there is no edge to hold.
 */
export function Tile({
  title,
  detail,
  icon,
  onClick,
  className,
}: {
  title: ReactNode;
  detail?: ReactNode;
  icon?: ReactNode;
  onClick?: () => void;
  /** Placement only — how the tile sits in the grid. */
  className?: string;
}) {
  const inner = (
    <>
      {/* The glyph takes the title's colour, not the muted one. Muted is for
          the line *under* the title — the thing being de-emphasised — and a
          glyph that names the card belongs with the name, not with the
          explanation. */}
      {icon ? (
        <span className="text-nf-ds-fg" aria-hidden="true">
          {icon}
        </span>
      ) : null}
      {/*
          TWO ROLES, ONE AT EACH WIDTH.

          Past rail width a tile sits beside the hero in the same row and has
          the room for the card title, so it takes it and the row reads as one
          set. On a phone the tiles are stacked full-width rows of a list and
          20/700 three times over is a wall of headings, so there they step
          down to the item role.

          A second role at a breakpoint rather than a size: both utilities
          carry their own leading, tracking and weight, so the pair stays on
          the scale at either width. It works because `nf-ds-card-title` is
          registered in `cn`'s font-size group — unregistered, tailwind-merge
          reads it as a colour and drops one of the two silently.
        */}
      <Title as="span" className="lg:text-nf-ds-card-title">
        {title}
      </Title>
      {detail ? (
        <Hint as="span" muted className="text-pretty">
          {detail}
        </Hint>
      ) : null}
    </>
  );

  /*
    ONE GAP FOR ALL THREE. The glyph sat 20 points above the title and the
    title 4 above its line, which reads as a glyph belonging to the card and
    a title belonging to the sentence — three things in two groups. They are
    one group: what this is, said three ways. So the text is not nested any
    more and the column spaces everything by `item` — 8 held them together
    but read as a single dense block at the card-title weight.
  */
  const shell = cn(
    "flex h-full w-full flex-col items-start justify-end gap-nf-ds-item text-left",
    className,
  );

  if (!onClick) return <div className={shell}>{inner}</div>;

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(shell, "outline-hidden", ROW_PRESS)}
    >
      {inner}
    </button>
  );
}

/**
 * A card whose whole surface is the button.
 *
 * Written down once because it was written out five times — `cn(SURFACE,
 * "rounded-nf-ds-card p-nf-ds-4 lg:p-nf-ds-6")`, at every destination on Home
 * — and a recipe repeated at five call sites is five places to forget when the
 * padding moves.
 */
export const CARD_ROW = cn(
  SURFACE,
  "rounded-nf-ds-card p-nf-ds-4 lg:p-nf-ds-6",
);

/**
 * The same card, tinted, for a row that is asking rather than offering.
 *
 * ONE RECIPE, COLOUR THE ONLY VARIABLE. A tinted row and a plain one have to
 * be the same object at the same weight or the page reads as two systems: the
 * fill is a tone at one alpha over the pane, and nothing else changes — no
 * border, no heavier shadow, no second radius. A drawn edge is what the
 * surface rules already refuse on a pane, and a tint that needs an outline to
 * be seen is a tint that is too weak.
 */
export const CARD_ROW_WARN = cn(
  CARD_ROW,
  // ALL THREE FILLS, not just the base one. `SURFACE` paints the pane once
  // plainly and twice more behind dark's `supports-[backdrop-filter]` and its
  // reduced-transparency fallback, and those are separate variant groups —
  // so a bare `bg-*` replaces only the light one and tailwind-merge keeps the
  // other two. That is what was already happening here: this row asked for a
  // tone and rendered the same grey as its neighbours in dark, with only the
  // icon coloured, which is why the tint looked like it had never been
  // applied. It had; it was being overwritten a variant later.
  "bg-nf-ds-tone-warn-bg",
  "dark:supports-[backdrop-filter]:bg-nf-ds-tone-warn-bg",
  "dark:[@media(prefers-reduced-transparency:reduce)]:bg-nf-ds-tone-warn-bg",
);

/**
 * The one action a screen is for.
 *
 * 52px and full width on a phone: this is the button a creator presses with a
 * thumb while holding the phone in the same hand, and it is the only control
 * on these screens that gets the whole width — a second one would mean
 * neither is the point.
 *
 * At rail width it returns to the surface's own control scale — 40px, 15px
 * text, sized to its label rather than to the screen. Full width there is not
 * emphasis, it is a button a metre long, and the 52px height was buying a
 * thumb's accuracy that a pointer does not need.
 */
export function Primary({
  children,
  onClick,
  disabled,
  icon,
  className,
}: {
  children: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  icon?: ReactNode;
  /** Placement only — where the caller needs it to sit in its own layout. */
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        // The button role, not a number. These were 16px on a phone and 12px
        // past rail width, which predated the decision that a button is a
        // pill — so the one screen where a field and a button sit together
        // showed a pill field beside a rounded rectangle, the asymmetry
        // backwards.
        "inline-flex h-nf-ds-control-lg w-full items-center justify-center gap-nf-ds-tight rounded-nf-ds-button",
        "lg:h-nf-ds-control lg:w-auto lg:px-nf-ds-5 lg:text-nf-ds-md",
        // `font-[600]`, not `font-semibold`. This app's `@theme` redefines
        // `--font-weight-semibold` as 300 and `bold` as 400, so the named
        // utilities render emphasis *lighter* than the body around them — this
        // label was coming out at 300 against a 400 page. The design system's
        // own `Button` goes numeric for the same reason and says so.
        "bg-nf-ds-brand font-nf-ds-sans text-nf-ds-md font-[600] leading-none text-nf-ds-white",
        "outline-hidden focus-visible:ring-2 focus-visible:ring-nf-ds-brand focus-visible:ring-offset-2",
        // The settled press, from `controls.ts`: 150ms, 0.97, transform and
        // colour, with the reduced-motion escape. This was a hand-written
        // 0.98 — close enough to look deliberate and still a second answer to
        // a question the system had already settled.
        CONTROL_PRESS,
        "disabled:pointer-events-none disabled:opacity-40",
        className,
      )}
    >
      {icon}
      {children}
    </button>
  );
}

/** The quieter sibling: same height, no fill. */
export function Secondary({
  children,
  icon,
  onClick,
  className,
}: {
  children: ReactNode;
  /** Leads the label, as on `Primary`. A brand mark, never decoration. */
  icon?: ReactNode;
  onClick?: () => void;
  /** Placement only — where the caller needs it to sit in its own layout. */
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        // The button role, not a number. These were 16px on a phone and 12px
        // past rail width, which predated the decision that a button is a
        // pill — so the one screen where a field and a button sit together
        // showed a pill field beside a rounded rectangle, the asymmetry
        // backwards.
        "inline-flex h-nf-ds-control-lg w-full items-center justify-center gap-nf-ds-tight rounded-nf-ds-button",
        "lg:h-nf-ds-control lg:w-auto lg:px-nf-ds-5 lg:text-nf-ds-md",
        "font-nf-ds-sans text-nf-ds-md font-medium leading-none text-nf-ds-fg",
        "ring-1 ring-nf-ds-surface-card-ring ring-inset outline-hidden",
        "focus-visible:ring-2 focus-visible:ring-nf-ds-brand",
        // The settled press, from `controls.ts`: 150ms, 0.97, transform and
        // colour, with the reduced-motion escape. This was a hand-written
        // 0.98 — close enough to look deliberate and still a second answer to
        // a question the system had already settled.
        CONTROL_PRESS,
        className,
      )}
    >
      {icon}
      {children}
    </button>
  );
}

/**
 * The third rung: no fill, no ring, just the label.
 *
 * `Primary` fills, `Secondary` rings, and a pair of header actions needs one
 * more step down so the important one of the two has something to stand out
 * against. Same height and radius as both, so a row of them lines up.
 *
 * Renders an anchor when given an `href`, because the one thing this is for
 * so far is a `mailto:` — and a button that sets `location.href` is a link
 * that cannot be copied, opened in a new tab, or read as a link by anything.
 */
export function Quiet({
  children,
  icon,
  onClick,
  href,
  className,
}: {
  children: ReactNode;
  icon?: ReactNode;
  onClick?: () => void;
  href?: string;
  /** Placement only. */
  className?: string;
}) {
  const shell = cn(
    "inline-flex h-nf-ds-control-lg w-full items-center justify-center gap-nf-ds-tight rounded-nf-ds-button",
    "lg:h-nf-ds-control lg:w-auto lg:px-nf-ds-4 lg:text-nf-ds-md",
    "font-nf-ds-sans text-nf-ds-md font-medium leading-none text-muted-foreground",
    "outline-hidden hover:text-nf-ds-fg focus-visible:ring-2 focus-visible:ring-nf-ds-brand",
    CONTROL_PRESS,
    className,
  );

  if (href) {
    return (
      <a href={href} className={shell}>
        {icon}
        {children}
      </a>
    );
  }

  return (
    <button type="button" onClick={onClick} className={shell}>
      {icon}
      {children}
    </button>
  );
}

export function Pill({
  children,
  tone = "idle",
  className,
  title,
}: {
  children: ReactNode;
  tone?: Tone;
  /** Placement and truncation only — never the badge's own geometry. */
  className?: string;
  /**
   * The full value, for a pill narrow enough to clip it.
   *
   * Only meaningful together with `truncate`: a badge normally holds one value
   * from a small set and has nothing to reveal, so this exists for the case
   * where the value is longer than the space and the reader still has to be
   * able to get at it.
   */
  title?: string;
}) {
  return (
    <span title={title} className={cn(BADGE, TONE[tone], className)}>
      {children}
    </span>
  );
}

export const MEDIA_CARD = [
  SURFACE,
  "group flex w-full max-w-[260px] flex-col overflow-hidden text-left outline-hidden",
  "transition-transform duration-150 ease-[cubic-bezier(0.23,1,0.32,1)] active:scale-[0.98] motion-reduce:transition-none",
  "focus-visible:ring-2 focus-visible:ring-nf-ds-brand focus-visible:ring-offset-2",
].join(" ");

export const MEDIA_POSTER = "aspect-[4/5] w-full rounded-none";

/**
 * A chip on the media itself — a rank, a duration, a take number.
 *
 * 12 points off its corner rather than 8: tight to the corner of a rounded
 * poster, a chip tracks the straight edge while the corner curves away from
 * it, which reads as misplaced even though it is square. Dark rather than a
 * tone, because it sits on a video frame whose colour is not ours to predict.
 */
export function Chip({
  children,
  className,
}: {
  children: ReactNode;
  /** Which corner, from the caller — the chip has no opinion. */
  className?: string;
}) {
  return (
    <Caption
      className={cn(
        "absolute rounded-[6px] bg-black/60 px-nf-ds-2 py-nf-ds-1 text-[10px] text-white",
        className,
      )}
    >
      {children}
    </Caption>
  );
}

/**
 * The number a card is about, with its name over it.
 *
 * One shape, no "secondary" variant. There was one, and it produced exactly
 * the problem it was meant to solve: two figures on one row, near enough in
 * weight to compete, far enough apart to read as a table. A card has one
 * figure; anything else it wants to say about money is a line of prose.
 */
/**
 * The figure treatment every big number on this surface shares.
 *
 * Exported because there is more than one of them — the balance on a card and
 * the payout under a tile on the shelf — and they have to be the same size or
 * the page has two competing ideas of what a headline number looks like.
 * Reading it from one place is what keeps them that way.
 *
 * 30 is a phone figure: a number has to carry a card seen at arm's length. At
 * rail width it steps back to the surface's own `Figure` role, which is 26.
 * The weight is numeric for the usual reason — `font-bold` is 400 here.
 */
export const FIGURE = cn(
  "font-nf-ds-sans font-[700] leading-[1.02] tracking-[-0.02em] tabular-nums",
  "text-[30px] lg:text-[26px]",
);

export function Money({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex min-w-0 flex-col gap-nf-ds-1">
      {/*
        NOT MUTED. The label was separated from its figure on three axes at
        once — 13 against 26, 500 against 700, and a muted colour on top —
        and three is one too many: it stopped reading as the name of the
        number and started reading as fine print that happened to be above
        it. Size and weight already say which of the two is the headline,
        which is the house rule's own pairing; the colour was spare.
      */}
      <Label>{label}</Label>
      <span className={FIGURE}>{value}</span>
    </div>
  );
}
